'use client';

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import {
  approveBusinessGovernancePolicy,
  approveBusinessMetricDefinition,
  createBusinessMetricDefinition,
  getBusinessGovernancePolicies,
  getBusinessMetricDefinitions,
  setBusinessGovernancePolicyActive,
  setBusinessMetricDefinitionActive,
  updateBusinessGovernancePolicy,
  updateBusinessMetricDefinition,
  type BusinessGovernancePolicy,
  type BusinessMetricDefinition,
  type BusinessMetricDefinitionInput,
} from '../lib/api/business-reports';
import { AdminAlertModal, Loader } from './common';
import {
  cleanSpaces,
  errorMessage,
  FinanceField,
  FinanceModal,
  FinanceStatus,
} from './BusinessReportFinanceUi';

type MetricForm = {
  metricKey: string;
  displayName: string;
  unit: string;
  targetOwner: string;
  permittedTargetSource: string;
  description: string;
  effectiveFrom: string;
  effectiveTo: string;
};

const EMPTY_METRIC: MetricForm = {
  metricKey: '',
  displayName: '',
  unit: '',
  targetOwner: '',
  permittedTargetSource: '',
  description: '',
  effectiveFrom: '',
  effectiveTo: '',
};

const DAILY_REVENUE_POLICY_KEY = 'BR-DAILY-REVENUE-SNAPSHOT';
const TARGET_DIMENSIONS_POLICY_KEY = 'BR-TARGET-DIMENSIONS';

type TargetDimensionOption = {
  key: string;
  displayName: string;
};

type TargetDimensionsPolicy = {
  businessDomains: TargetDimensionOption[];
  accountableTeams: TargetDimensionOption[];
};

type LaunchDateTreatment =
  | 'EXCLUDE_PRE_LAUNCH'
  | 'INCLUDE_PRE_LAUNCH';

type BenchmarkDivisorTreatment =
  | 'ACTIVE_QUARTER_PERIODS'
  | 'FULL_ACTIVE_QUARTER_PERIODS'
  | 'FIXED_THREE_MONTHS';

type DailyRevenueLaunchPolicy = {
  launchDateTreatment: LaunchDateTreatment;
  benchmarkDivisorTreatment: BenchmarkDivisorTreatment;
};

type PreviousMonthPeriodTreatment =
  | 'COMPLETE_PREVIOUS_CALENDAR_MONTH'
  | 'SAME_ELAPSED_DAY_PREVIOUS_MONTH';

type DailyRevenuePreviousMonthPolicy = {
  previousMonthPeriodTreatment: PreviousMonthPeriodTreatment;
};

type LandingFormulaTreatment =
  | 'PRIOR_YEAR_SAME_MONTH_CURVE'
  | 'APPROVED_OVERRIDE_ONLY'
  | 'DISABLED';

type LandingExceptionTreatment =
  | 'RETURN_UNAVAILABLE'
  | 'REQUIRE_APPROVED_OVERRIDE';

type LandingDateAlignment =
  | 'SAME_DAY_OR_MONTH_END'
  | 'STRICT_SAME_DAY';

type LandingOverridePrecedence =
  | 'APPROVED_OVERRIDE_FIRST'
  | 'CALCULATED_LANDING_ONLY';

type DailyRevenueLandingPolicy = {
  landingFormulaTreatment: LandingFormulaTreatment;
  priorYearSameDayZeroTreatment: LandingExceptionTreatment;
  priorYearDataUnavailableTreatment: LandingExceptionTreatment;
  priorYearCenterPreLaunchTreatment: LandingExceptionTreatment;
  landingDateAlignment: LandingDateAlignment;
  landingOverridePrecedence: LandingOverridePrecedence;
};

type MoneyDisplayScale =
  | 'RUPEES'
  | 'THOUSANDS'
  | 'LAKHS'
  | 'CRORES';

type DailyRevenueMoneyPolicy = {
  currency: string;
  storageScale: 'SOURCE_AMOUNT';
  summaryDisplayScale: MoneyDisplayScale;
  detailDisplayScale: MoneyDisplayScale;
  summaryDecimalPlaces: number;
  detailDecimalPlaces: number;
};

const APPROVED_INITIAL_LAUNCH_POLICY: DailyRevenueLaunchPolicy = {
  launchDateTreatment: 'EXCLUDE_PRE_LAUNCH',
  benchmarkDivisorTreatment: 'ACTIVE_QUARTER_PERIODS',
};

const APPROVED_INITIAL_PREVIOUS_MONTH_POLICY: DailyRevenuePreviousMonthPolicy = {
  previousMonthPeriodTreatment: 'COMPLETE_PREVIOUS_CALENDAR_MONTH',
};

const APPROVED_INITIAL_LANDING_POLICY: DailyRevenueLandingPolicy = {
  landingFormulaTreatment: 'PRIOR_YEAR_SAME_MONTH_CURVE',
  priorYearSameDayZeroTreatment: 'REQUIRE_APPROVED_OVERRIDE',
  priorYearDataUnavailableTreatment: 'REQUIRE_APPROVED_OVERRIDE',
  priorYearCenterPreLaunchTreatment: 'REQUIRE_APPROVED_OVERRIDE',
  landingDateAlignment: 'SAME_DAY_OR_MONTH_END',
  landingOverridePrecedence: 'APPROVED_OVERRIDE_FIRST',
};

const APPROVED_INITIAL_MONEY_POLICY: DailyRevenueMoneyPolicy = {
  currency: 'INR',
  storageScale: 'SOURCE_AMOUNT',
  summaryDisplayScale: 'LAKHS',
  detailDisplayScale: 'LAKHS',
  summaryDecimalPlaces: 2,
  detailDecimalPlaces: 2,
};

type ApprovalDialog =
  | { kind: 'metric'; record: BusinessMetricDefinition; reference: string }
  | { kind: 'policy'; record: BusinessGovernancePolicy; reference: string };

export function BusinessReportGovernanceTab({
  active,
  token,
  userName,
  onTopBarActionsChange,
}: {
  active: boolean;
  token?: string;
  userName: string;
  onTopBarActionsChange?: (actions: ReactNode) => void;
}) {
  const [metrics, setMetrics] = useState<BusinessMetricDefinition[]>([]);
  const [policies, setPolicies] = useState<BusinessGovernancePolicy[]>([]);
  const [editingMetric, setEditingMetric] = useState<BusinessMetricDefinition | null | undefined>(undefined);
  const [metricForm, setMetricForm] = useState<MetricForm>(EMPTY_METRIC);
  const [editingPolicy, setEditingPolicy] = useState<BusinessGovernancePolicy | null>(null);
  const [approval, setApproval] = useState<ApprovalDialog | null>(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; title: string; message: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [metricRows, policyRows] = await Promise.all([
        getBusinessMetricDefinitions(token),
        getBusinessGovernancePolicies(token),
      ]);
      setMetrics(metricRows);
      setPolicies(policyRows);
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not load Target Governance', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (active) void load();
  }, [active, load]);

  const openMetric = useCallback((metric: BusinessMetricDefinition | null) => {
    setMetricForm(metric ? {
      metricKey: metric.metricKey,
      displayName: metric.displayName,
      unit: metric.unit,
      targetOwner: metric.targetOwner,
      permittedTargetSource: metric.permittedTargetSource,
      description: metric.description || '',
      effectiveFrom: dateInput(metric.effectiveFrom),
      effectiveTo: dateInput(metric.effectiveTo),
    } : EMPTY_METRIC);
    setEditingMetric(metric);
  }, []);

  useEffect(() => {
    if (!active || !onTopBarActionsChange) return undefined;
    onTopBarActionsChange(
      <button className="btn btn-primary compact-btn" aria-label="Add Metric Definition" onClick={() => openMetric(null)}>
        + Add Metric Definition
      </button>,
    );
    return undefined;
  }, [active, onTopBarActionsChange, openMetric]);

  if (!active) return null;

  const dailyRevenuePolicy = policies.find(
    (policy) => policy.policyKey === DAILY_REVENUE_POLICY_KEY,
  );
  const dailyLaunchPolicy = readDailyRevenueLaunchPolicy(
    dailyRevenuePolicy?.configuration,
  );
  const dailyMoneyPolicy = readDailyRevenueMoneyPolicy(
    dailyRevenuePolicy?.configuration,
  );
  const dailyPreviousMonthPolicy = readDailyRevenuePreviousMonthPolicy(
    dailyRevenuePolicy?.configuration,
  );
  const dailyLandingPolicy = readDailyRevenueLandingPolicy(
    dailyRevenuePolicy?.configuration,
  );
  const targetDimensionsPolicy = policies.find(
    (policy) => policy.policyKey === TARGET_DIMENSIONS_POLICY_KEY,
  );
  const targetDimensions = readTargetDimensionsPolicy(
    targetDimensionsPolicy?.configuration,
  );

  const saveMetric = async () => {
    setLoading(true);
    try {
      const input = metricInput(metricForm);
      if (editingMetric) await updateBusinessMetricDefinition(editingMetric.metricKey, input, token);
      else await createBusinessMetricDefinition(input, token);
      setEditingMetric(undefined);
      await load();
      setAlert({
        type: 'success',
        title: editingMetric ? 'Metric definition updated' : 'Metric definition created',
        message: 'The definition is draft and inactive. A different administrator must approve it before activation.',
      });
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not save Metric Definition', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const savePolicy = async () => {
    if (!editingPolicy) return;
    setLoading(true);
    try {
      await updateBusinessGovernancePolicy(editingPolicy.policyKey, {
        policyName: cleanSpaces(editingPolicy.policyName),
        description: cleanSpaces(editingPolicy.description || '') || null,
        effectiveFrom: dateInput(editingPolicy.effectiveFrom) || null,
        effectiveTo: dateInput(editingPolicy.effectiveTo) || null,
        makerCheckerRequired: editingPolicy.makerCheckerRequired,
        ...(editingPolicy.policyKey === DAILY_REVENUE_POLICY_KEY ||
        editingPolicy.policyKey === TARGET_DIMENSIONS_POLICY_KEY
          ? { configuration: editingPolicy.configuration }
          : {}),
      }, token);
      setEditingPolicy(null);
      await load();
      setAlert({
        type: 'success',
        title: 'Policy updated',
        message: 'The policy is draft and inactive. A different administrator must approve it before activation.',
      });
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not save Governance Policy', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const approve = async () => {
    if (!approval) return;
    const reference = cleanSpaces(approval.reference);
    if (!reference) {
      setAlert({ type: 'error', title: 'Approval reference required', message: 'Enter the approved document, ticket, or decision reference.' });
      return;
    }
    setLoading(true);
    try {
      if (approval.kind === 'metric') {
        await approveBusinessMetricDefinition(approval.record.metricKey, reference, token);
      } else {
        await approveBusinessGovernancePolicy(approval.record.policyKey, reference, token);
      }
      setApproval(null);
      await load();
      setAlert({ type: 'success', title: 'Governance record approved', message: 'Approval evidence is recorded. Activation remains a separate explicit action.' });
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not approve Governance Record', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const toggleMetric = async (metric: BusinessMetricDefinition) => {
    if (!window.confirm(`${metric.isActive ? 'Deactivate' : 'Activate'} ${metric.displayName}? This is enforced and audited by the API and database.`)) return;
    setLoading(true);
    try {
      await setBusinessMetricDefinitionActive(metric.metricKey, !metric.isActive, token);
      await load();
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not change Metric status', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const togglePolicy = async (policy: BusinessGovernancePolicy) => {
    const dependentFeature = policy.policyKey === DAILY_REVENUE_POLICY_KEY
      ? 'Daily Revenue calculations depend on this server-side version.'
      : 'Landing override requests depend on this server-side state.';
    if (!window.confirm(`${policy.isActive ? 'Deactivate' : 'Activate'} ${policy.policyKey}? ${dependentFeature}`)) return;
    setLoading(true);
    try {
      await setBusinessGovernancePolicyActive(policy.policyKey, !policy.isActive, token);
      await load();
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not change Policy status', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="tab-content business-source-page" id="tab-business-report-governance">
      {loading && <Loader message="Processing Target Governance..." />}

      <div className="business-finance-policy-banner success">
        <strong>Approved configuration protected</strong>
        <span>Current approved versions remain active. Saving a change creates an inactive draft that requires approval by a different administrator before explicit activation.</span>
      </div>

      <div className="enterprise-card business-launch-policy-card">
        <div className="enterprise-card-heading">
          <div>
            <strong>Projected Landing Formula & Exceptions</strong>
            <span>Forecast month-end performance from the prior year's same-month achievement curve. The former calendar-day run rate is not used.</span>
          </div>
          {dailyRevenuePolicy && (
            <div className="business-finance-row-actions">
              <FinanceStatus value={dailyRevenuePolicy.approvalStatus} />
              <FinanceStatus value={dailyRevenuePolicy.isActive ? 'ACTIVE' : 'INACTIVE'} />
              <button className="btn btn-sm target-action is-edit" onClick={() => setEditingPolicy({ ...dailyRevenuePolicy })}>
                Edit Landing Formula
              </button>
            </div>
          )}
        </div>
        {dailyRevenuePolicy ? (
          <div className="business-launch-policy-body">
            <div className="business-launch-policy-grid">
              <PolicyDecision
                label="Automatic formula"
                value={landingFormulaLabel(dailyLandingPolicy.landingFormulaTreatment)}
                detail="Current-year MTD × (prior-year complete same-month actual ÷ prior-year same-month MTD through the aligned day)."
              />
              <PolicyDecision
                label="Date alignment"
                value={landingDateAlignmentLabel(dailyLandingPolicy.landingDateAlignment)}
                detail="Controls 29 February and other dates that do not exist in the comparison year."
              />
              <PolicyDecision
                label="Approved BR-0-09 override"
                value={landingOverrideLabel(dailyLandingPolicy.landingOverridePrecedence)}
                detail="Only a complete approved maker-checker override can replace the automatic result."
              />
              <PolicyDecision
                label="Zero prior-year same-day MTD"
                value={landingExceptionLabel(dailyLandingPolicy.priorYearSameDayZeroTreatment)}
                detail="Division by zero is never attempted."
              />
              <PolicyDecision
                label="Missing prior-year data"
                value={landingExceptionLabel(dailyLandingPolicy.priorYearDataUnavailableTreatment)}
                detail="Applies when either the comparison-date MTD or complete comparison month is unavailable."
              />
              <PolicyDecision
                label="Centre not launched in prior year"
                value={landingExceptionLabel(dailyLandingPolicy.priorYearCenterPreLaunchTreatment)}
                detail="A centre without an active prior-year comparison is never projected from fabricated history."
              />
            </div>
            <div className="business-launch-policy-example">
              <strong>25 July 2026 example</strong>
              <span>Current MTD 50 × (July 2025 full month 60 ÷ July 2025 MTD through 25 July 40) = projected landing 75.</span>
            </div>
            <div className="business-launch-policy-example">
              <strong>5 July 2026 example</strong>
              <span>Current MTD 10 × (July 2025 full month 60 ÷ July 2025 MTD through 5 July 10) = projected landing 60.</span>
            </div>
            <div className="business-launch-policy-meta">
              <span>Formula version <strong>{readFormulaVersion(dailyRevenuePolicy.configuration)}</strong></span>
              <span>Policy version <strong>{dailyRevenuePolicy.version}</strong></span>
              <span>Approval reference <strong>{dailyRevenuePolicy.approvalReference || 'Pending full-policy approval'}</strong></span>
            </div>
          </div>
        ) : (
          <div className="empty-cell">The Daily Revenue governance policy is missing. Projected landing remains unavailable.</div>
        )}
      </div>

      <div className="enterprise-card business-launch-policy-card">
        <div className="enterprise-card-heading">
          <div>
            <strong>Previous-Month Actual Policy</strong>
            <span>Governed period used for the Previous Month Actual column. Growth vs LM separately uses the automatically aligned previous-month period. Neither calculation uses targets or projected landing.</span>
          </div>
          {dailyRevenuePolicy && (
            <div className="business-finance-row-actions">
              <FinanceStatus value={dailyRevenuePolicy.approvalStatus} />
              <FinanceStatus value={dailyRevenuePolicy.isActive ? 'ACTIVE' : 'INACTIVE'} />
              <button className="btn btn-sm target-action is-edit" onClick={() => setEditingPolicy({ ...dailyRevenuePolicy })}>
                Edit Previous-Month Policy
              </button>
            </div>
          )}
        </div>
        {dailyRevenuePolicy ? (
          <div className="business-launch-policy-body">
            <div className="business-launch-policy-grid">
              <PolicyDecision
                label="Comparison period"
                value={previousMonthTreatmentLabel(dailyPreviousMonthPolicy.previousMonthPeriodTreatment)}
                detail={previousMonthTreatmentDetail(dailyPreviousMonthPolicy.previousMonthPeriodTreatment)}
              />
                <PolicyDecision
                  label="Included values"
                  value="Actual governed inputs only"
                  detail="Approved/imported report inputs are aggregated. Monthly targets and projected landing are never included. Growth vs LM compares current MTD with the corresponding elapsed period of the previous month."
              />
              <PolicyDecision
                label="Centre launch boundary"
                value="Launch policy still applies"
                detail="Rows before each Centre Master launch date remain excluded when pre-launch exclusion is selected."
              />
            </div>
            <div className="business-launch-policy-example">
              <strong>Preview — report date 15 July 2026</strong>
              <span>{previousMonthPolicyPreview(dailyPreviousMonthPolicy.previousMonthPeriodTreatment)}</span>
            </div>
            <div className="business-launch-policy-meta">
              <span>Policy version <strong>{dailyRevenuePolicy.version}</strong></span>
              <span>Approval reference <strong>{dailyRevenuePolicy.approvalReference || 'Pending full-policy approval'}</strong></span>
              <span>Effective <strong>{dateRangeLabel(dailyRevenuePolicy.effectiveFrom, dailyRevenuePolicy.effectiveTo)}</strong></span>
            </div>
          </div>
        ) : (
          <div className="empty-cell">The Daily Revenue governance policy is missing. Previous-month calculation activation remains blocked.</div>
        )}
      </div>

      <div className="enterprise-card business-launch-policy-card">
        <div className="enterprise-card-heading">
          <div>
            <strong>Daily Revenue Launch-Date Policy</strong>
            <span>Governed treatment of pre-launch dates and partial launch months. No revenue is estimated or prorated.</span>
          </div>
          {dailyRevenuePolicy && (
            <div className="business-finance-row-actions">
              <FinanceStatus value={dailyRevenuePolicy.approvalStatus} />
              <FinanceStatus value={dailyRevenuePolicy.isActive ? 'ACTIVE' : 'INACTIVE'} />
              <button className="btn btn-sm target-action is-edit" onClick={() => setEditingPolicy({ ...dailyRevenuePolicy })}>
                Edit Launch Policy
              </button>
            </div>
          )}
        </div>
        {dailyRevenuePolicy ? (
          <div className="business-launch-policy-body">
            <div className="business-launch-policy-grid">
              <PolicyDecision
                label="Before launch date"
                value={dailyLaunchPolicy.launchDateTreatment === 'EXCLUDE_PRE_LAUNCH' ? 'Excluded' : 'Included'}
                detail={dailyLaunchPolicy.launchDateTreatment === 'EXCLUDE_PRE_LAUNCH'
                  ? 'Rows dated before the Centre Master launch date do not enter calculations.'
                  : 'Pre-launch rows are included when source data contains them.'}
              />
              <PolicyDecision
                label="Partial launch month"
                value={benchmarkTreatmentLabel(dailyLaunchPolicy.benchmarkDivisorTreatment)}
                detail={benchmarkTreatmentDetail(dailyLaunchPolicy.benchmarkDivisorTreatment)}
              />
              <PolicyDecision
                label="Estimation / proration"
                value="Never applied"
                detail="Only actual governed source values are used. Missing pre-launch days are not estimated."
              />
            </div>
            <div className="business-launch-policy-example">
              <strong>Current configured behavior — Vizag launched 8 October</strong>
              <span>{launchPolicyPreview(dailyLaunchPolicy)}</span>
            </div>
            <div className="business-launch-policy-meta">
              <span>Policy version <strong>{dailyRevenuePolicy.version}</strong></span>
              <span>Approval reference <strong>{dailyRevenuePolicy.approvalReference || 'Pending full-policy approval'}</strong></span>
              <span>Effective <strong>{dateRangeLabel(dailyRevenuePolicy.effectiveFrom, dailyRevenuePolicy.effectiveTo)}</strong></span>
            </div>
          </div>
        ) : (
          <div className="empty-cell">The Daily Revenue governance policy is missing. Report activation remains blocked.</div>
        )}
      </div>

      <div className="enterprise-card business-launch-policy-card">
        <div className="enterprise-card-heading">
          <div>
            <strong>Daily Revenue Currency & Display Policy</strong>
            <span>Governed source storage and presentation scaling. Currency symbols received from Looker do not determine the currency.</span>
          </div>
          {dailyRevenuePolicy && (
            <div className="business-finance-row-actions">
              <FinanceStatus value={dailyRevenuePolicy.approvalStatus} />
              <FinanceStatus value={dailyRevenuePolicy.isActive ? 'ACTIVE' : 'INACTIVE'} />
              <button className="btn btn-sm target-action is-edit" onClick={() => setEditingPolicy({ ...dailyRevenuePolicy })}>
                Edit Currency & Display
              </button>
            </div>
          )}
        </div>
        {dailyRevenuePolicy ? (
          <div className="business-launch-policy-body">
            <div className="business-launch-policy-grid business-money-policy-grid">
              <PolicyDecision
                label="Approved currency"
                value={dailyMoneyPolicy.currency}
                detail="The ISO currency code is authoritative even when a Looker visualization shows another symbol."
              />
              <PolicyDecision
                label="Database storage"
                value="Full source amount"
                detail="The numeric Looker amount is stored without display scaling or currency conversion."
              />
              <PolicyDecision
                label="Dashboard summaries"
                value={moneyScaleLabel(dailyMoneyPolicy.summaryDisplayScale)}
                detail={`${dailyMoneyPolicy.summaryDecimalPlaces} decimal place${dailyMoneyPolicy.summaryDecimalPlaces === 1 ? '' : 's'} in KPI and centre summary values.`}
              />
              <PolicyDecision
                label="Detailed data / export"
                value={moneyScaleLabel(dailyMoneyPolicy.detailDisplayScale)}
                detail={`${dailyMoneyPolicy.detailDecimalPlaces} decimal place${dailyMoneyPolicy.detailDecimalPlaces === 1 ? '' : 's'}; the approved initial choice matches portal and export presentation in lakhs.`}
              />
            </div>
            <div className="business-launch-policy-example">
              <strong>Live presentation preview — source amount 1,250,000.50</strong>
              <span>Dashboard: {formatMoneyPolicyPreview(1_250_000.5, dailyMoneyPolicy, 'summary')} · Detailed data: {formatMoneyPolicyPreview(1_250_000.5, dailyMoneyPolicy, 'detail')}</span>
            </div>
            <div className="business-launch-policy-meta">
              <span>No exchange-rate conversion</span>
              <span>Policy version <strong>{dailyRevenuePolicy.version}</strong></span>
              <span>Effective <strong>{dateRangeLabel(dailyRevenuePolicy.effectiveFrom, dailyRevenuePolicy.effectiveTo)}</strong></span>
            </div>
          </div>
        ) : (
          <div className="empty-cell">The Daily Revenue governance policy is missing. Currency and display activation remain blocked.</div>
        )}
      </div>

      <div className="enterprise-card business-launch-policy-card">
        <div className="enterprise-card-heading">
          <div>
            <strong>Monthly Target Domains & Teams</strong>
            <span>Governed dimensions for centre-level targets. B2C/B2B and accountable teams are maintained as policy data, not hardcoded into target forms.</span>
          </div>
          {targetDimensionsPolicy && (
            <div className="business-finance-row-actions">
              <FinanceStatus value={targetDimensionsPolicy.approvalStatus} />
              <FinanceStatus value={targetDimensionsPolicy.isActive ? 'ACTIVE' : 'INACTIVE'} />
              <button className="btn btn-sm target-action is-edit" onClick={() => setEditingPolicy({ ...targetDimensionsPolicy })}>
                Edit Domains & Teams
              </button>
            </div>
          )}
        </div>
        {targetDimensionsPolicy ? (
          <div className="business-launch-policy-body">
            <div className="business-launch-policy-grid">
              <PolicyDecision
                label="Business domains"
                value={`${targetDimensions.businessDomains.length} configured`}
                detail={targetDimensions.businessDomains.map((entry) => entry.displayName).join(', ')}
              />
              <PolicyDecision
                label="Accountable teams"
                value={`${targetDimensions.accountableTeams.length} configured`}
                detail={targetDimensions.accountableTeams.map((entry) => entry.displayName).join(', ')}
              />
              <PolicyDecision
                label="Target uniqueness"
                value="Month + centre + metric + domain + team"
                detail="The metric definition supplies the governed measurement unit, owner, source and approval evidence."
              />
            </div>
            <div className="business-launch-policy-meta">
              <span>Policy version <strong>{targetDimensionsPolicy.version}</strong></span>
              <span>Approval reference <strong>{targetDimensionsPolicy.approvalReference || 'Pending approval'}</strong></span>
              <span>Effective <strong>{dateRangeLabel(targetDimensionsPolicy.effectiveFrom, targetDimensionsPolicy.effectiveTo)}</strong></span>
            </div>
          </div>
        ) : (
          <div className="empty-cell">The Target Dimensions policy is missing. Dimensioned target writes remain blocked.</div>
        )}
      </div>

      <div className="enterprise-card enterprise-table-card target-governance-table-card">
        <div className="enterprise-card-heading"><div><strong>Metric Definition Master</strong><span>Only active, approved, currently effective definitions can authorize target writes.</span></div></div>
        <div className="enterprise-table-wrap">
          <table className="enterprise-table target-governance-table">
            <thead><tr><th>Metric</th><th>Unit</th><th>Owner / Source</th><th>Version</th><th>Approval</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {metrics.map((metric) => (
                <tr key={metric.id}>
                  <td><strong>{metric.displayName}</strong><small className="business-finance-subline">{metric.metricKey}</small></td>
                  <td>{metric.unit}</td>
                  <td>{metric.targetOwner}<small className="business-finance-subline">{metric.permittedTargetSource}</small></td>
                  <td>{metric.version}</td>
                  <td><FinanceStatus value={metric.approvalStatus} /><small className="business-finance-subline">{metric.approvalReference || 'No approval reference'}</small></td>
                  <td><FinanceStatus value={metric.isActive ? 'ACTIVE' : 'INACTIVE'} /></td>
                  <td><div className="business-finance-row-actions target-governance-actions">
                    <button className="btn btn-sm target-action is-edit" onClick={() => openMetric(metric)}>Edit</button>
                    <button className="btn btn-sm target-action is-approve" disabled={metric.updatedBy === userName || metric.approvalStatus === 'APPROVED'} title={metric.updatedBy === userName ? 'Maker cannot approve their own change' : undefined} onClick={() => setApproval({ kind: 'metric', record: metric, reference: '' })}>Approve</button>
                    <button className={`btn btn-sm target-action ${metric.isActive ? 'is-deactivate' : 'is-activate'}`} disabled={!metric.isActive && metric.approvalStatus !== 'APPROVED'} onClick={() => void toggleMetric(metric)}>{metric.isActive ? 'Deactivate' : 'Activate'}</button>
                  </div></td>
                </tr>
              ))}
              {!metrics.length && <tr><td colSpan={7} className="empty-cell">No metric definitions exist. Monthly target creation is blocked by the API.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="enterprise-card enterprise-table-card target-governance-table-card">
        <div className="enterprise-card-heading"><div><strong>Governance Policies</strong><span>Daily Revenue calculations and BR-0-09 landing overrides are both controlled by approved server-side policy versions.</span></div></div>
        <div className="enterprise-table-wrap">
          <table className="enterprise-table target-governance-table target-policy-register-table">
            <thead><tr><th>Policy</th><th>Maker-checker</th><th>Version</th><th>Approval</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>{policies.map((policy) => (
              <tr key={policy.id}>
                <td><strong>{policy.policyName}</strong><small className="business-finance-subline">{policy.policyKey}</small></td>
                <td>{policy.makerCheckerRequired ? 'Required' : 'Not required'}</td>
                <td>{policy.version}</td>
                <td><FinanceStatus value={policy.approvalStatus} /><small className="business-finance-subline">{policy.approvalReference || 'No approval reference'}</small></td>
                <td><FinanceStatus value={policy.isActive ? 'ACTIVE' : 'INACTIVE'} /></td>
                <td><div className="business-finance-row-actions target-governance-actions">
                  <button className="btn btn-sm target-action is-edit" onClick={() => setEditingPolicy({ ...policy })}>Edit</button>
                  <button className="btn btn-sm target-action is-approve" disabled={policy.updatedBy === userName || policy.approvalStatus === 'APPROVED'} title={policy.updatedBy === userName ? 'Maker cannot approve their own change' : undefined} onClick={() => setApproval({ kind: 'policy', record: policy, reference: '' })}>Approve</button>
                  <button className={`btn btn-sm target-action ${policy.isActive ? 'is-deactivate' : 'is-activate'}`} disabled={!policy.isActive && policy.approvalStatus !== 'APPROVED'} onClick={() => void togglePolicy(policy)}>{policy.isActive ? 'Deactivate' : 'Activate'}</button>
                </div></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>

      {editingMetric !== undefined && (
        <FinanceModal title={editingMetric ? 'Edit Metric Definition' : 'Add Metric Definition'} subtitle="Saving creates a new inactive draft version." wide onClose={() => setEditingMetric(undefined)} footer={<><span>Metric key is immutable after creation.</span><div><button className="btn btn-outline" onClick={() => setEditingMetric(undefined)}>Cancel</button><button className="btn btn-primary" onClick={() => void saveMetric()}>Save Draft</button></div></>}>
          <div className="business-source-form-grid">
            <FinanceField label="Metric Key"><input value={metricForm.metricKey} disabled={Boolean(editingMetric)} onChange={(event) => setMetricForm((current) => ({ ...current, metricKey: event.target.value.toUpperCase() }))} /></FinanceField>
            <FinanceField label="Display Name"><input value={metricForm.displayName} onChange={(event) => setMetricForm((current) => ({ ...current, displayName: event.target.value }))} /></FinanceField>
            <FinanceField label="Unit"><input value={metricForm.unit} placeholder="For example INR or COUNT" onChange={(event) => setMetricForm((current) => ({ ...current, unit: event.target.value }))} /></FinanceField>
            <FinanceField label="Target Owner"><input value={metricForm.targetOwner} onChange={(event) => setMetricForm((current) => ({ ...current, targetOwner: event.target.value }))} /></FinanceField>
            <FinanceField label="Permitted Target Source"><input value={metricForm.permittedTargetSource} onChange={(event) => setMetricForm((current) => ({ ...current, permittedTargetSource: event.target.value }))} /></FinanceField>
            <FinanceField label="Description" full><textarea rows={3} value={metricForm.description} onChange={(event) => setMetricForm((current) => ({ ...current, description: event.target.value }))} /></FinanceField>
            <FinanceField label="Effective From"><input type="date" value={metricForm.effectiveFrom} onChange={(event) => setMetricForm((current) => ({ ...current, effectiveFrom: event.target.value }))} /></FinanceField>
            <FinanceField label="Effective To"><input type="date" value={metricForm.effectiveTo} onChange={(event) => setMetricForm((current) => ({ ...current, effectiveTo: event.target.value }))} /></FinanceField>
          </div>
        </FinanceModal>
      )}

      {editingPolicy && (
        <FinanceModal title={`Edit ${editingPolicy.policyKey}`} subtitle="Saving creates a new inactive draft policy version and clears earlier approval evidence." wide onClose={() => setEditingPolicy(null)} footer={<><span>Governed financial policies retain maker-checker.</span><div><button className="btn btn-outline" onClick={() => setEditingPolicy(null)}>Cancel</button><button className="btn btn-primary" onClick={() => void savePolicy()}>Save Draft</button></div></>}>
          <div className="business-source-form-grid">
            <FinanceField label="Policy Key"><input value={editingPolicy.policyKey} disabled /></FinanceField>
            <FinanceField label="Policy Name"><input value={editingPolicy.policyName} onChange={(event) => setEditingPolicy((current) => current ? { ...current, policyName: event.target.value } : current)} /></FinanceField>
            <FinanceField label="Description" full><textarea rows={4} value={editingPolicy.description || ''} onChange={(event) => setEditingPolicy((current) => current ? { ...current, description: event.target.value } : current)} /></FinanceField>
            <FinanceField label="Effective From"><input type="date" value={dateInput(editingPolicy.effectiveFrom)} onChange={(event) => setEditingPolicy((current) => current ? { ...current, effectiveFrom: event.target.value || null } : current)} /></FinanceField>
            <FinanceField label="Effective To"><input type="date" value={dateInput(editingPolicy.effectiveTo)} onChange={(event) => setEditingPolicy((current) => current ? { ...current, effectiveTo: event.target.value || null } : current)} /></FinanceField>
            <label className="business-finance-confirm full"><input type="checkbox" checked={editingPolicy.makerCheckerRequired} disabled={editingPolicy.policyKey === 'BR-0-09' || editingPolicy.policyKey === DAILY_REVENUE_POLICY_KEY || editingPolicy.policyKey === TARGET_DIMENSIONS_POLICY_KEY} onChange={(event) => setEditingPolicy((current) => current ? { ...current, makerCheckerRequired: event.target.checked } : current)} /><span>Require a different maker and checker.</span></label>
            {editingPolicy.policyKey === DAILY_REVENUE_POLICY_KEY && (
              <>
                <div className="business-finance-policy-banner full">
                  <strong>Launch-date calculation controls</strong>
                  <span>The API validates these values again before the complete Daily Revenue policy can be activated.</span>
                </div>
                <FinanceField label="Pre-launch Data Treatment" full>
                  <select
                    value={readDailyRevenueLaunchPolicy(editingPolicy.configuration).launchDateTreatment}
                    onChange={(event) => setEditingPolicy((current) => current
                      ? {
                          ...current,
                          configuration: writeDailyRevenueLaunchPolicy(current.configuration, {
                            launchDateTreatment: event.target.value as LaunchDateTreatment,
                          }),
                        }
                      : current)}
                  >
                    <option value="EXCLUDE_PRE_LAUNCH">Exclude all dates before launch (approved initial choice)</option>
                    <option value="INCLUDE_PRE_LAUNCH">Include pre-launch source rows</option>
                  </select>
                  <small>The Centre Master Launch Date is the boundary. Effective From/To controls policy validity, not centre opening.</small>
                </FinanceField>
                <FinanceField label="Partial Launch Month in Benchmark Averages" full>
                  <select
                    value={readDailyRevenueLaunchPolicy(editingPolicy.configuration).benchmarkDivisorTreatment}
                    onChange={(event) => setEditingPolicy((current) => current
                      ? {
                          ...current,
                          configuration: writeDailyRevenueLaunchPolicy(current.configuration, {
                            benchmarkDivisorTreatment: event.target.value as BenchmarkDivisorTreatment,
                          }),
                        }
                      : current)}
                  >
                    <option value="ACTIVE_QUARTER_PERIODS">Include actual launch-month values and count the month (approved initial choice)</option>
                    <option value="FULL_ACTIVE_QUARTER_PERIODS">Exclude a partial launch month and begin with the first full benchmark month</option>
                    <option value="FIXED_THREE_MONTHS">Always divide the quarter by three months</option>
                  </select>
                  <small>No option prorates or estimates missing days. The active/full-active selections change both benchmark totals and the divisor where applicable.</small>
                </FinanceField>
                <div className="business-launch-policy-example full">
                  <strong>Preview — Vizag launched 8 October</strong>
                  <span>{launchPolicyPreview(readDailyRevenueLaunchPolicy(editingPolicy.configuration))}</span>
                </div>
                <div className="business-finance-policy-banner full">
                  <strong>Previous-month actual controls</strong>
                  <span>This controls the Previous Month Actual column only. Growth vs LM always compares current MTD with the aligned previous-month period. Targets and projected landing never enter either calculation, and the launch-date policy remains applicable.</span>
                </div>
                <FinanceField label="Previous Month Comparison Period" full>
                  <select
                    value={readDailyRevenuePreviousMonthPolicy(editingPolicy.configuration).previousMonthPeriodTreatment}
                    onChange={(event) => setEditingPolicy((current) => current
                      ? {
                          ...current,
                          configuration: writeDailyRevenuePreviousMonthPolicy(current.configuration, {
                            previousMonthPeriodTreatment: event.target.value as PreviousMonthPeriodTreatment,
                          }),
                        }
                      : current)}
                  >
                    <option value="COMPLETE_PREVIOUS_CALENDAR_MONTH">Complete previous calendar month (approved default)</option>
                    <option value="SAME_ELAPSED_DAY_PREVIOUS_MONTH">Previous month through the same elapsed day</option>
                  </select>
                  <small>The same-day option changes the displayed Previous Month Actual column. Growth vs LM already uses the corresponding elapsed period regardless of this column setting.</small>
                </FinanceField>
                <div className="business-launch-policy-example full">
                  <strong>Preview — report date 15 July 2026</strong>
                  <span>{previousMonthPolicyPreview(readDailyRevenuePreviousMonthPolicy(editingPolicy.configuration).previousMonthPeriodTreatment)}</span>
                </div>
                <div className="business-finance-policy-banner full">
                  <strong>Projected Landing formula and exception controls</strong>
                  <span>The former calendar-day run rate has been removed. Configuration is limited to reviewed options and never accepts executable formula text.</span>
                </div>
                <FinanceField label="Projected Landing Method" full>
                  <select
                    value={readDailyRevenueLandingPolicy(editingPolicy.configuration).landingFormulaTreatment}
                    onChange={(event) => setEditingPolicy((current) => current
                      ? {
                          ...current,
                          configuration: writeDailyRevenueLandingPolicy(current.configuration, {
                            landingFormulaTreatment: event.target.value as LandingFormulaTreatment,
                            ...(event.target.value === 'APPROVED_OVERRIDE_ONLY'
                              ? { landingOverridePrecedence: 'APPROVED_OVERRIDE_FIRST' as const }
                              : {}),
                          }),
                        }
                      : current)}
                  >
                    <option value="PRIOR_YEAR_SAME_MONTH_CURVE">Prior-year same-month performance curve (approved formula)</option>
                    <option value="APPROVED_OVERRIDE_ONLY">Approved BR-0-09 override only</option>
                    <option value="DISABLED">Landing disabled</option>
                  </select>
                  <small>Curve formula: current-year MTD × (prior-year complete same-month actual ÷ prior-year same-month MTD through the aligned day).</small>
                </FinanceField>
                <FinanceField label="Leap-Year / Month-End Alignment" full>
                  <select
                    value={readDailyRevenueLandingPolicy(editingPolicy.configuration).landingDateAlignment}
                    onChange={(event) => setEditingPolicy((current) => current
                      ? {
                          ...current,
                          configuration: writeDailyRevenueLandingPolicy(current.configuration, {
                            landingDateAlignment: event.target.value as LandingDateAlignment,
                          }),
                        }
                      : current)}
                  >
                    <option value="SAME_DAY_OR_MONTH_END">Same day; clamp to comparison month end</option>
                    <option value="STRICT_SAME_DAY">Require the exact same day or apply missing-data treatment</option>
                  </select>
                  <small>For 29 February versus a non-leap comparison year, clamp uses 28 February; strict mode treats the reference as unavailable.</small>
                </FinanceField>
                <FinanceField label="If Prior-Year Same-Day MTD Is Zero">
                  <select
                    value={readDailyRevenueLandingPolicy(editingPolicy.configuration).priorYearSameDayZeroTreatment}
                    onChange={(event) => setEditingPolicy((current) => current
                      ? {
                          ...current,
                          configuration: writeDailyRevenueLandingPolicy(current.configuration, {
                            priorYearSameDayZeroTreatment: event.target.value as LandingExceptionTreatment,
                          }),
                        }
                      : current)}
                  >
                    <LandingExceptionOptions />
                  </select>
                </FinanceField>
                <FinanceField label="If Prior-Year Data Is Missing">
                  <select
                    value={readDailyRevenueLandingPolicy(editingPolicy.configuration).priorYearDataUnavailableTreatment}
                    onChange={(event) => setEditingPolicy((current) => current
                      ? {
                          ...current,
                          configuration: writeDailyRevenueLandingPolicy(current.configuration, {
                            priorYearDataUnavailableTreatment: event.target.value as LandingExceptionTreatment,
                          }),
                        }
                      : current)}
                  >
                    <LandingExceptionOptions />
                  </select>
                </FinanceField>
                <FinanceField label="If Centre Was Not Launched in Comparison Year">
                  <select
                    value={readDailyRevenueLandingPolicy(editingPolicy.configuration).priorYearCenterPreLaunchTreatment}
                    onChange={(event) => setEditingPolicy((current) => current
                      ? {
                          ...current,
                          configuration: writeDailyRevenueLandingPolicy(current.configuration, {
                            priorYearCenterPreLaunchTreatment: event.target.value as LandingExceptionTreatment,
                          }),
                        }
                      : current)}
                  >
                    <LandingExceptionOptions />
                  </select>
                </FinanceField>
                <FinanceField label="Approved BR-0-09 Override Precedence">
                  <select
                    value={readDailyRevenueLandingPolicy(editingPolicy.configuration).landingOverridePrecedence}
                    disabled={readDailyRevenueLandingPolicy(editingPolicy.configuration).landingFormulaTreatment === 'APPROVED_OVERRIDE_ONLY'}
                    onChange={(event) => setEditingPolicy((current) => current
                      ? {
                          ...current,
                          configuration: writeDailyRevenueLandingPolicy(current.configuration, {
                            landingOverridePrecedence: event.target.value as LandingOverridePrecedence,
                          }),
                        }
                      : current)}
                  >
                    <option value="APPROVED_OVERRIDE_FIRST">Approved override replaces calculated landing</option>
                    <option value="CALCULATED_LANDING_ONLY">Ignore override and use calculated landing only</option>
                  </select>
                </FinanceField>
                <div className="business-launch-policy-example full">
                  <strong>Live examples</strong>
                  <span>25 July: 50 × (60 ÷ 40) = 75 · 5 July: 10 × (60 ÷ 10) = 60.</span>
                </div>
                <div className="business-finance-policy-banner full">
                  <strong>Currency and display controls</strong>
                  <span>Source values remain unscaled in storage. These controls affect presentation only and never perform exchange-rate conversion.</span>
                </div>
                <FinanceField label="Currency (ISO code)" full>
                  <input
                    value={readDailyRevenueCurrencyDraft(editingPolicy.configuration)}
                    maxLength={3}
                    inputMode="text"
                    onChange={(event) => setEditingPolicy((current) => current
                      ? {
                          ...current,
                          configuration: writeDailyRevenueMoneyPolicy(current.configuration, {
                            currency: event.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3),
                          }),
                        }
                      : current)}
                    aria-describedby="daily-revenue-currency-help"
                  />
                  <small id="daily-revenue-currency-help">Initial approved value: INR. A Looker dollar symbol is ignored; the mapped numeric value is treated as this governed currency.</small>
                </FinanceField>
                <FinanceField label="Database Storage" full>
                  <input value="Store the full source amount without scaling" disabled />
                  <small>For INR, 1,250,000.50 from Looker is stored as 1,250,000.50. Portal, Excel and future PDF scaling are display transformations only.</small>
                </FinanceField>
                <FinanceField label="Dashboard Summary Scale">
                  <select
                    value={readDailyRevenueMoneyPolicy(editingPolicy.configuration).summaryDisplayScale}
                    onChange={(event) => setEditingPolicy((current) => current
                      ? {
                          ...current,
                          configuration: writeDailyRevenueMoneyPolicy(current.configuration, {
                            summaryDisplayScale: event.target.value as MoneyDisplayScale,
                          }),
                        }
                      : current)}
                  >
                    <MoneyScaleOptions />
                  </select>
                </FinanceField>
                <FinanceField label="Dashboard Decimal Places">
                  <select
                    value={readDailyRevenueMoneyPolicy(editingPolicy.configuration).summaryDecimalPlaces}
                    onChange={(event) => setEditingPolicy((current) => current
                      ? {
                          ...current,
                          configuration: writeDailyRevenueMoneyPolicy(current.configuration, {
                            summaryDecimalPlaces: Number(event.target.value),
                          }),
                        }
                      : current)}
                  >
                    <DecimalPlaceOptions />
                  </select>
                </FinanceField>
                <FinanceField label="Detailed Data / Export Scale">
                  <select
                    value={readDailyRevenueMoneyPolicy(editingPolicy.configuration).detailDisplayScale}
                    onChange={(event) => setEditingPolicy((current) => current
                      ? {
                          ...current,
                          configuration: writeDailyRevenueMoneyPolicy(current.configuration, {
                            detailDisplayScale: event.target.value as MoneyDisplayScale,
                          }),
                        }
                      : current)}
                  >
                    <MoneyScaleOptions />
                  </select>
                </FinanceField>
                <FinanceField label="Detailed Decimal Places">
                  <select
                    value={readDailyRevenueMoneyPolicy(editingPolicy.configuration).detailDecimalPlaces}
                    onChange={(event) => setEditingPolicy((current) => current
                      ? {
                          ...current,
                          configuration: writeDailyRevenueMoneyPolicy(current.configuration, {
                            detailDecimalPlaces: Number(event.target.value),
                          }),
                        }
                      : current)}
                  >
                    <DecimalPlaceOptions />
                  </select>
                </FinanceField>
                <div className="business-launch-policy-example full">
                  <strong>Preview — source amount 1,250,000.50</strong>
                  <span>Dashboard: {formatMoneyPolicyPreview(1_250_000.5, readDailyRevenueMoneyPolicy(editingPolicy.configuration), 'summary')} · Detailed data: {formatMoneyPolicyPreview(1_250_000.5, readDailyRevenueMoneyPolicy(editingPolicy.configuration), 'detail')}</span>
                </div>
              </>
            )}
            {editingPolicy.policyKey === TARGET_DIMENSIONS_POLICY_KEY && (
              <>
                <div className="business-finance-policy-banner full">
                  <strong>Dynamic monthly-target dimensions</strong>
                  <span>Enter one governed option per line as KEY | Display Name. Saving creates an inactive draft; another user must approve it before activation.</span>
                </div>
                <FinanceField label="Business Domains" full>
                  <textarea
                    rows={7}
                    value={dimensionLines(readTargetDimensionsPolicy(editingPolicy.configuration).businessDomains)}
                    onChange={(event) => setEditingPolicy((current) => current
                      ? {
                          ...current,
                          configuration: writeTargetDimensionList(
                            current.configuration,
                            'businessDomains',
                            event.target.value,
                          ),
                        }
                      : current)}
                    placeholder={'ORGANIZATION | All Business\nB2C | B2C\nB2B | B2B'}
                  />
                  <small>Keep ORGANIZATION for centre-wide targets. Keys remain stable references; display names may be changed through a new governed version.</small>
                </FinanceField>
                <FinanceField label="Accountable Teams" full>
                  <textarea
                    rows={8}
                    value={dimensionLines(readTargetDimensionsPolicy(editingPolicy.configuration).accountableTeams)}
                    onChange={(event) => setEditingPolicy((current) => current
                      ? {
                          ...current,
                          configuration: writeTargetDimensionList(
                            current.configuration,
                            'accountableTeams',
                            event.target.value,
                          ),
                        }
                      : current)}
                    placeholder={'ALL_TEAMS | All Teams\nBUSINESS | Business\nOPERATIONS | Operations\nCLINICAL | Clinical\nPHARMACY | Pharmacy'}
                  />
                  <small>Keep ALL_TEAMS for the overall Daily Revenue target. Add future teams here instead of changing application code.</small>
                </FinanceField>
                <div className="business-launch-policy-example full">
                  <strong>Current draft scope</strong>
                  <span>
                    {readTargetDimensionsPolicy(editingPolicy.configuration).businessDomains.length} business domain(s) ·{' '}
                    {readTargetDimensionsPolicy(editingPolicy.configuration).accountableTeams.length} accountable team(s)
                  </span>
                </div>
              </>
            )}
          </div>
        </FinanceModal>
      )}

      {approval && (
        <FinanceModal title={`Approve ${approval.kind === 'metric' ? approval.record.metricKey : approval.record.policyKey}`} subtitle="Your authenticated identity and this approval reference will be stored." onClose={() => setApproval(null)} footer={<><span>Activation remains a separate action.</span><div><button className="btn btn-outline" onClick={() => setApproval(null)}>Cancel</button><button className="btn btn-primary" onClick={() => void approve()}>Record Approval</button></div></>}>
          <FinanceField label="Approval Reference" full><input value={approval.reference} onChange={(event) => setApproval({ ...approval, reference: event.target.value })} placeholder="Approved document, ticket, or decision ID" /></FinanceField>
        </FinanceModal>
      )}

      {alert && <AdminAlertModal title={alert.title} message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}
    </section>
  );
}

function metricInput(form: MetricForm): BusinessMetricDefinitionInput {
  const metricKey = cleanSpaces(form.metricKey).toUpperCase().replace(/[^A-Z0-9]+/g, '_');
  const displayName = cleanSpaces(form.displayName);
  const unit = cleanSpaces(form.unit);
  const targetOwner = cleanSpaces(form.targetOwner);
  const permittedTargetSource = cleanSpaces(form.permittedTargetSource);
  if (!metricKey || !displayName || !unit || !targetOwner || !permittedTargetSource) {
    throw new Error('Metric key, display name, unit, target owner, and permitted target source are required.');
  }
  if (form.effectiveFrom && form.effectiveTo && form.effectiveTo < form.effectiveFrom) {
    throw new Error('Effective To must be on or after Effective From.');
  }
  return {
    metricKey,
    displayName,
    unit,
    targetOwner,
    permittedTargetSource,
    description: cleanSpaces(form.description) || null,
    effectiveFrom: form.effectiveFrom || null,
    effectiveTo: form.effectiveTo || null,
  };
}

function dateInput(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '';
}

function readTargetDimensionsPolicy(
  configuration: Record<string, unknown> | undefined,
): TargetDimensionsPolicy {
  const fallback: TargetDimensionsPolicy = {
    businessDomains: [
      { key: 'ORGANIZATION', displayName: 'All Business' },
      { key: 'B2C', displayName: 'B2C' },
      { key: 'B2B', displayName: 'B2B' },
    ],
    accountableTeams: [
      { key: 'ALL_TEAMS', displayName: 'All Teams' },
      { key: 'BUSINESS', displayName: 'Business' },
      { key: 'OPERATIONS', displayName: 'Operations' },
      { key: 'CLINICAL', displayName: 'Clinical' },
      { key: 'PHARMACY', displayName: 'Pharmacy' },
    ],
  };
  const readList = (value: unknown, fallbackValue: TargetDimensionOption[]) => {
    if (!Array.isArray(value)) return fallbackValue;
    const entries = value.flatMap((entry) => {
      const row = objectValue(entry);
      const key = typeof row.key === 'string' ? row.key.trim().toUpperCase() : '';
      const displayName = typeof row.displayName === 'string' ? cleanSpaces(row.displayName) : '';
      return key && displayName ? [{ key, displayName }] : [];
    });
    return entries.length ? entries : fallbackValue;
  };
  return {
    businessDomains: readList(configuration?.businessDomains, fallback.businessDomains),
    accountableTeams: readList(configuration?.accountableTeams, fallback.accountableTeams),
  };
}

function dimensionLines(options: TargetDimensionOption[]): string {
  return options.map((option) => `${option.key} | ${option.displayName}`).join('\n');
}

function parseDimensionLines(value: string): TargetDimensionOption[] {
  return value.split(/\r?\n/).flatMap((line) => {
    const separator = line.indexOf('|');
    if (separator < 0) return [];
    const key = line.slice(0, separator).trim().toUpperCase().replace(/[^A-Z0-9_]/g, '_');
    const displayName = cleanSpaces(line.slice(separator + 1));
    return key && displayName ? [{ key, displayName }] : [];
  });
}

function writeTargetDimensionList(
  configuration: Record<string, unknown>,
  field: keyof TargetDimensionsPolicy,
  value: string,
): Record<string, unknown> {
  return {
    ...configuration,
    schemaVersion: 1,
    [field]: parseDimensionLines(value),
  };
}

function PolicyDecision({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div className="business-launch-policy-decision">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  );
}

function readDailyRevenueLaunchPolicy(
  configuration: Record<string, unknown> | undefined,
): DailyRevenueLaunchPolicy {
  const formula = objectValue(configuration?.formula);
  const launchDateTreatment = formula.launchDateTreatment;
  const benchmarkDivisorTreatment = formula.benchmarkDivisorTreatment;
  return {
    launchDateTreatment:
      launchDateTreatment === 'INCLUDE_PRE_LAUNCH' ||
      launchDateTreatment === 'EXCLUDE_PRE_LAUNCH'
        ? launchDateTreatment
        : APPROVED_INITIAL_LAUNCH_POLICY.launchDateTreatment,
    benchmarkDivisorTreatment:
      benchmarkDivisorTreatment === 'FIXED_THREE_MONTHS' ||
      benchmarkDivisorTreatment === 'ACTIVE_QUARTER_PERIODS' ||
      benchmarkDivisorTreatment === 'FULL_ACTIVE_QUARTER_PERIODS'
        ? benchmarkDivisorTreatment
        : APPROVED_INITIAL_LAUNCH_POLICY.benchmarkDivisorTreatment,
  };
}

function writeDailyRevenueLaunchPolicy(
  configuration: Record<string, unknown>,
  patch: Partial<DailyRevenueLaunchPolicy>,
): Record<string, unknown> {
  const current = readDailyRevenueLaunchPolicy(configuration);
  return {
    ...configuration,
    schemaVersion: 1,
    formula: {
      ...objectValue(configuration.formula),
      ...current,
      ...patch,
    },
  };
}

function readDailyRevenuePreviousMonthPolicy(
  configuration: Record<string, unknown> | undefined,
): DailyRevenuePreviousMonthPolicy {
  const treatment = objectValue(configuration?.formula)
    .previousMonthPeriodTreatment;
  return {
    previousMonthPeriodTreatment:
      treatment === 'COMPLETE_PREVIOUS_CALENDAR_MONTH' ||
      treatment === 'SAME_ELAPSED_DAY_PREVIOUS_MONTH'
        ? treatment
        : APPROVED_INITIAL_PREVIOUS_MONTH_POLICY.previousMonthPeriodTreatment,
  };
}

function writeDailyRevenuePreviousMonthPolicy(
  configuration: Record<string, unknown>,
  patch: Partial<DailyRevenuePreviousMonthPolicy>,
): Record<string, unknown> {
  return {
    ...configuration,
    schemaVersion: 1,
    formula: {
      ...objectValue(configuration.formula),
      ...readDailyRevenuePreviousMonthPolicy(configuration),
      ...patch,
    },
  };
}

function readDailyRevenueLandingPolicy(
  configuration: Record<string, unknown> | undefined,
): DailyRevenueLandingPolicy {
  const formula = objectValue(configuration?.formula);
  const landingFormulaTreatment = formula.landingFormulaTreatment;
  const priorYearSameDayZeroTreatment = formula.priorYearSameDayZeroTreatment;
  const priorYearDataUnavailableTreatment = formula.priorYearDataUnavailableTreatment;
  const priorYearCenterPreLaunchTreatment = formula.priorYearCenterPreLaunchTreatment;
  const landingDateAlignment = formula.landingDateAlignment;
  const landingOverridePrecedence = formula.landingOverridePrecedence;
  return {
    landingFormulaTreatment:
      landingFormulaTreatment === 'PRIOR_YEAR_SAME_MONTH_CURVE' ||
      landingFormulaTreatment === 'APPROVED_OVERRIDE_ONLY' ||
      landingFormulaTreatment === 'DISABLED'
        ? landingFormulaTreatment
        : APPROVED_INITIAL_LANDING_POLICY.landingFormulaTreatment,
    priorYearSameDayZeroTreatment: isLandingExceptionTreatment(
      priorYearSameDayZeroTreatment,
    )
      ? priorYearSameDayZeroTreatment
      : APPROVED_INITIAL_LANDING_POLICY.priorYearSameDayZeroTreatment,
    priorYearDataUnavailableTreatment: isLandingExceptionTreatment(
      priorYearDataUnavailableTreatment,
    )
      ? priorYearDataUnavailableTreatment
      : APPROVED_INITIAL_LANDING_POLICY.priorYearDataUnavailableTreatment,
    priorYearCenterPreLaunchTreatment: isLandingExceptionTreatment(
      priorYearCenterPreLaunchTreatment,
    )
      ? priorYearCenterPreLaunchTreatment
      : APPROVED_INITIAL_LANDING_POLICY.priorYearCenterPreLaunchTreatment,
    landingDateAlignment:
      landingDateAlignment === 'SAME_DAY_OR_MONTH_END' ||
      landingDateAlignment === 'STRICT_SAME_DAY'
        ? landingDateAlignment
        : APPROVED_INITIAL_LANDING_POLICY.landingDateAlignment,
    landingOverridePrecedence:
      landingOverridePrecedence === 'APPROVED_OVERRIDE_FIRST' ||
      landingOverridePrecedence === 'CALCULATED_LANDING_ONLY'
        ? landingOverridePrecedence
        : APPROVED_INITIAL_LANDING_POLICY.landingOverridePrecedence,
  };
}

function writeDailyRevenueLandingPolicy(
  configuration: Record<string, unknown>,
  patch: Partial<DailyRevenueLandingPolicy>,
): Record<string, unknown> {
  return {
    ...configuration,
    schemaVersion: 1,
    formula: {
      ...objectValue(configuration.formula),
      ...readDailyRevenueLandingPolicy(configuration),
      ...patch,
      formulaVersion: 'FERTY9_DAILY_REVENUE_V3_PRIOR_YEAR_CURVE',
    },
  };
}

function isLandingExceptionTreatment(
  value: unknown,
): value is LandingExceptionTreatment {
  return value === 'RETURN_UNAVAILABLE' ||
    value === 'REQUIRE_APPROVED_OVERRIDE';
}

function LandingExceptionOptions() {
  return (
    <>
      <option value="REQUIRE_APPROVED_OVERRIDE">Require an approved BR-0-09 override</option>
      <option value="RETURN_UNAVAILABLE">Show landing as unavailable</option>
    </>
  );
}

function landingFormulaLabel(value: LandingFormulaTreatment): string {
  if (value === 'APPROVED_OVERRIDE_ONLY') return 'Approved override only';
  if (value === 'DISABLED') return 'Landing disabled';
  return 'Prior-year same-month curve';
}

function landingExceptionLabel(value: LandingExceptionTreatment): string {
  return value === 'REQUIRE_APPROVED_OVERRIDE'
    ? 'Approved override required'
    : 'Landing unavailable';
}

function landingDateAlignmentLabel(value: LandingDateAlignment): string {
  return value === 'STRICT_SAME_DAY'
    ? 'Exact same day required'
    : 'Same day, clamped to month end';
}

function landingOverrideLabel(value: LandingOverridePrecedence): string {
  return value === 'APPROVED_OVERRIDE_FIRST'
    ? 'Approved override takes precedence'
    : 'Calculated landing only';
}

function readFormulaVersion(
  configuration: Record<string, unknown>,
): string {
  const value = objectValue(configuration.formula).formulaVersion;
  return typeof value === 'string'
    ? value
    : 'FERTY9_DAILY_REVENUE_V3_PRIOR_YEAR_CURVE';
}

function previousMonthTreatmentLabel(
  treatment: PreviousMonthPeriodTreatment,
): string {
  return treatment === 'COMPLETE_PREVIOUS_CALENDAR_MONTH'
    ? 'Complete previous calendar month'
    : 'Previous month through same day';
}

function previousMonthTreatmentDetail(
  treatment: PreviousMonthPeriodTreatment,
): string {
  return treatment === 'COMPLETE_PREVIOUS_CALENDAR_MONTH'
    ? 'Uses day 1 through the final calendar day of the immediately preceding month.'
    : 'Uses day 1 through the matching elapsed day of the immediately preceding month, capped at that month end.';
}

function previousMonthPolicyPreview(
  treatment: PreviousMonthPeriodTreatment,
): string {
  return treatment === 'COMPLETE_PREVIOUS_CALENDAR_MONTH'
    ? '1 June–30 June 2026 actuals are included.'
    : '1 June–15 June 2026 actuals are included.';
}

function readDailyRevenueMoneyPolicy(
  configuration: Record<string, unknown> | undefined,
): DailyRevenueMoneyPolicy {
  const assembly = objectValue(configuration?.assembly);
  const presentation = objectValue(assembly.moneyPresentation);
  const currency = typeof assembly.currency === 'string'
    ? assembly.currency.trim().toUpperCase()
    : '';
  const summaryDisplayScale = presentation.summaryDisplayScale;
  const detailDisplayScale = presentation.detailDisplayScale;
  const summaryDecimalPlaces = Number(presentation.summaryDecimalPlaces);
  const detailDecimalPlaces = Number(presentation.detailDecimalPlaces);
  return {
    currency: /^[A-Z]{3}$/.test(currency)
      ? currency
      : APPROVED_INITIAL_MONEY_POLICY.currency,
    storageScale: 'SOURCE_AMOUNT',
    summaryDisplayScale: isMoneyDisplayScale(summaryDisplayScale)
      ? summaryDisplayScale
      : APPROVED_INITIAL_MONEY_POLICY.summaryDisplayScale,
    detailDisplayScale: isMoneyDisplayScale(detailDisplayScale)
      ? detailDisplayScale
      : APPROVED_INITIAL_MONEY_POLICY.detailDisplayScale,
    summaryDecimalPlaces: isDecimalPlaces(summaryDecimalPlaces)
      ? summaryDecimalPlaces
      : APPROVED_INITIAL_MONEY_POLICY.summaryDecimalPlaces,
    detailDecimalPlaces: isDecimalPlaces(detailDecimalPlaces)
      ? detailDecimalPlaces
      : APPROVED_INITIAL_MONEY_POLICY.detailDecimalPlaces,
  };
}

function writeDailyRevenueMoneyPolicy(
  configuration: Record<string, unknown>,
  patch: Partial<DailyRevenueMoneyPolicy>,
): Record<string, unknown> {
  const currentPolicy = readDailyRevenueMoneyPolicy(configuration);
  const current = {
    ...currentPolicy,
    ...patch,
    currency: patch.currency === undefined
      ? currentPolicy.currency
      : patch.currency,
  };
  return {
    ...configuration,
    schemaVersion: 1,
    assembly: {
      ...objectValue(configuration.assembly),
      currency: current.currency,
      moneyPresentation: {
        storageScale: 'SOURCE_AMOUNT',
        summaryDisplayScale: current.summaryDisplayScale,
        detailDisplayScale: current.detailDisplayScale,
        summaryDecimalPlaces: current.summaryDecimalPlaces,
        detailDecimalPlaces: current.detailDecimalPlaces,
      },
    },
  };
}

function readDailyRevenueCurrencyDraft(
  configuration: Record<string, unknown>,
): string {
  const currency = objectValue(configuration.assembly).currency;
  return typeof currency === 'string'
    ? currency.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3)
    : APPROVED_INITIAL_MONEY_POLICY.currency;
}

function isMoneyDisplayScale(value: unknown): value is MoneyDisplayScale {
  return value === 'RUPEES' ||
    value === 'THOUSANDS' ||
    value === 'LAKHS' ||
    value === 'CRORES';
}

function isDecimalPlaces(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= 4;
}

function MoneyScaleOptions() {
  return (
    <>
      <option value="RUPEES">Full units / rupees (no scaling)</option>
      <option value="THOUSANDS">Thousands (÷ 1,000)</option>
      <option value="LAKHS">Lakhs (÷ 1,00,000)</option>
      <option value="CRORES">Crores (÷ 1,00,00,000)</option>
    </>
  );
}

function DecimalPlaceOptions() {
  return (
    <>
      {[0, 1, 2, 3, 4].map((value) => (
        <option key={value} value={value}>{value}</option>
      ))}
    </>
  );
}

function formatMoneyPolicyPreview(
  amount: number,
  policy: DailyRevenueMoneyPolicy,
  context: 'summary' | 'detail',
): string {
  const scale = context === 'summary'
    ? policy.summaryDisplayScale
    : policy.detailDisplayScale;
  const decimals = context === 'summary'
    ? policy.summaryDecimalPlaces
    : policy.detailDecimalPlaces;
  const value = amount / moneyScaleDivisor(scale);
  const suffix = moneyScaleSuffix(scale);
  return `${policy.currency} ${value.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}${suffix ? ` ${suffix}` : ''}`;
}

function moneyScaleDivisor(value: MoneyDisplayScale): number {
  if (value === 'THOUSANDS') return 1_000;
  if (value === 'LAKHS') return 100_000;
  if (value === 'CRORES') return 10_000_000;
  return 1;
}

function moneyScaleSuffix(value: MoneyDisplayScale): string {
  if (value === 'THOUSANDS') return 'K';
  if (value === 'LAKHS') return 'L';
  if (value === 'CRORES') return 'Cr';
  return '';
}

function moneyScaleLabel(value: MoneyDisplayScale): string {
  if (value === 'THOUSANDS') return 'Thousands';
  if (value === 'LAKHS') return 'Lakhs';
  if (value === 'CRORES') return 'Crores';
  return 'Full rupees';
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function benchmarkTreatmentLabel(value: BenchmarkDivisorTreatment): string {
  if (value === 'ACTIVE_QUARTER_PERIODS') return 'Include actual; count active month';
  if (value === 'FULL_ACTIVE_QUARTER_PERIODS') return 'Exclude until first full month';
  return 'Fixed three-month divisor';
}

function benchmarkTreatmentDetail(value: BenchmarkDivisorTreatment): string {
  if (value === 'ACTIVE_QUARTER_PERIODS') {
    return 'Actual values from launch onward are included and the launch month counts once.';
  }
  if (value === 'FULL_ACTIVE_QUARTER_PERIODS') {
    return 'The partial launch month is omitted from benchmark totals and the divisor.';
  }
  return 'The benchmark is divided by three even when the centre was not active for all three periods.';
}

function launchPolicyPreview(policy: DailyRevenueLaunchPolicy): string {
  const beforeLaunch = policy.launchDateTreatment === 'EXCLUDE_PRE_LAUNCH'
    ? 'Dates before 8 October are excluded.'
    : 'Any source rows before 8 October are included.';
  if (policy.benchmarkDivisorTreatment === 'ACTIVE_QUARTER_PERIODS') {
    return `${beforeLaunch} Actual values from 8–31 October are included and October counts as one active benchmark month. No missing days are estimated.`;
  }
  if (policy.benchmarkDivisorTreatment === 'FULL_ACTIVE_QUARTER_PERIODS') {
    return `${beforeLaunch} October is excluded from benchmark averages; the first full benchmark month begins in November. No missing days are estimated.`;
  }
  return `${beforeLaunch} Actual values from launch onward are used, but the quarter average always uses a divisor of three. No missing days are estimated.`;
}

function dateRangeLabel(from: string | null, to: string | null): string {
  if (!from && !to) return 'Not yet set';
  return `${dateInput(from) || 'Open'} – ${dateInput(to) || 'Open'}`;
}
